import axios from 'axios';
import { toast } from 'sonner';
import { t } from 'i18next';
import { AuthApiErrorCode } from '../models/auth';
import { XimiSsoUnavailableError } from './ximi/ximiSsoSession';
import type { XimiSessionLostCodeValue } from '../stores/ximi/ximiStimulinSession';

/** Codes 401 signalant une preuve SSO Ximi manquante/invalide (≠ access token Stimul'In). */
const SSO_PROOF_ERROR_CODES: readonly string[] = ['SSO_REQUIRED', 'SSO_INVALID', 'SSO_MISMATCH'];

/** 401 dont le code désigne la preuve SSO Ximi (et non l'access token Stimul'In). */
export const isXimiSsoProofError = (error: unknown): boolean =>
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    SSO_PROOF_ERROR_CODES.includes(error.response?.data?.code);

/**
 * 401 du refresh : StimulinAPI a fermé la session faute de preuve de session valide (accès
 * retiré, jeton altéré). L'éviction par un autre appareil, elle, est constatée avant, sur le
 * 403 d'Ozmo — d'où un message générique ici.
 */
export const isSessionProofInvalidError = (error: unknown): boolean =>
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    error.response?.data?.code === AuthApiErrorCode.SESSION_PROOF_INVALID;

/** Valeur historique de `?ximiError=` : bandeau générique (aucun code métier à message dédié). */
export const XIMI_SESSION_LOST_GENERIC_REASON = '1';

/** Ozmo a répondu « session perdue » AVANT le refresh : on n'appelle pas StimulinAPI. */
export class XimiSessionLostError extends Error {
    constructor(public readonly code: XimiSessionLostCodeValue) {
        super(`Ximi session lost: ${code}`);
    }
}

/** La preuve Ozmo n'a pas pu être obtenue (route injoignable) : refresh impossible pour l'instant. */
export class RefreshUnavailableError extends Error {
    constructor(reason: string) {
        super(`Refresh unavailable: ${reason}`);
    }
}

/**
 * Échec sans verdict d'authentification : réseau, serveur (5xx), rate-limit, ou une dépendance
 * Xelya injoignable. La session doit survivre — l'utilisateur ne doit jamais se reconnecter
 * pour un problème d'infrastructure ; seule une réponse HTTP explicite (4xx) purge la session.
 */
export const isTransientFailure = (error: unknown): boolean =>
    error instanceof RefreshUnavailableError ||
    error instanceof XimiSsoUnavailableError ||
    (axios.isAxiosError(error) &&
        (!error.response || error.response.status >= 500 || error.response.status === 429));

/** Un seul toast par fenêtre — évite le spam si plusieurs appels tombent en même temps. */
const TOAST_THROTTLE_MS = 10_000;

const throttled = (show: () => void): (() => void) => {
    let lastAt = 0;
    return () => {
        const now = Date.now();
        if (now - lastAt < TOAST_THROTTLE_MS) return;
        lastAt = now;
        show();
    };
};

export const notifyRateLimited = throttled(() =>
    toast.warning(t('Trop de requêtes. Veuillez patienter un instant.'))
);

export const notifyUnavailable = throttled(() =>
    toast.error(t("L'opération n'a pas pu être effectuée : problème réseau ou serveur. Réessayez."))
);
